import { CodeHighlightNode, CodeNode } from "@lexical/code-core"
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html"
import { AutoLinkNode, LinkNode } from "@lexical/link"
import { ListItemNode, ListNode } from "@lexical/list"
import { TRANSFORMERS } from "@lexical/markdown"
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin"
import { LexicalComposer } from "@lexical/react/LexicalComposer"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { ContentEditable } from "@lexical/react/LexicalContentEditable"
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin"
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode"
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin"
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin"
import { ListPlugin } from "@lexical/react/LexicalListPlugin"
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin"
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin"
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin"
import { HeadingNode, QuoteNode } from "@lexical/rich-text"
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table"
import cn from "classnames"
import { $getRoot } from "lexical"
import "prismjs"
import { useEffect, useRef } from "react"

import AutoLinkPlugin from "./plugins/AutoLinkPlugin"
import CmdEnterPlugin from "./plugins/CmdEnterPlugin"
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin"
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin"
import ToolbarPlugin from "./plugins/ToolbarPlugin"
import { RichTextEditorProps } from "./RichTextEditor.types"
import ExampleTheme from "./themes/theme"

import styles from "./RichTextEditor.module.scss"

function LoadHtmlPlugin({ html }: { html: string }) {
  const [editor] = useLexicalComposerContext()

  // Only run when html prop changes, not on every render
  useEffect(() => {
    editor.update(() => {
      let nodes = []

      const parser = new DOMParser()
      const dom = parser.parseFromString(html, "text/html")

      nodes = $generateNodesFromDOM(editor, dom)

      const root = $getRoot()

      root.clear()
      root.append(...nodes)
    })
  }, [html, editor])

  return null
}

export const RichTextEditor = (props: RichTextEditorProps) => {
  const {
    children,
    html,
    extraTools,
    disabled,
    noStyles,
    classes = {},
    nodes = [],
    toolbar = true
  } = props

  const editorConfig = useRef({
    theme: ExampleTheme,
    namespace: "",
    onError(error: Error) {
      throw error
    },
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
      HorizontalRuleNode,
      ...nodes
    ]
  }).current

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <div
        className={cn(
          "editor-container",
          !noStyles && styles.elements,
          classes.container,
          classes.elements
        )}
      >
        {toolbar && (
          <ToolbarPlugin disabled={disabled} extraTools={extraTools} />
        )}
        <div className={cn("editor-inner", classes.inner)}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                contentEditable={!disabled}
                className={cn("editor-input", classes.input)}
              />
            }
            placeholder={null}
            ErrorBoundary={() => null}
          />
          {!children && (
            <>
              <LoadHtmlPlugin html={html} />
              <AutoFocusPlugin />
            </>
          )}
          <HistoryPlugin />
          <OnChangePlugin
            onChange={(_, editor) => {
              editor.update(() => {
                const raw = $generateHtmlFromNodes(editor, null)

                const unwrapNestedListItems = (element: Element) => {
                  element
                    .querySelectorAll("li.editor-nested-listitem")
                    .forEach((li) => {
                      li.classList.remove(
                        "editor-nested-listitem",
                        "editor-listitem"
                      )
                    })

                  element
                    .querySelectorAll("li.editor-listitem")
                    .forEach((li) => {
                      li.classList.remove("editor-listitem")
                    })

                  element
                    .querySelectorAll("ol.editor-list-ol")
                    .forEach((ol) => {
                      ol.classList.remove("editor-list-ol")
                    })

                  element
                    .querySelectorAll("ul.editor-list-ul")
                    .forEach((ul) => {
                      ul.classList.remove("editor-list-ul")
                    })

                  element.querySelectorAll("[class='']").forEach((el) => {
                    el.removeAttribute("class")
                  })
                }

                const parser = new DOMParser()
                const dom = parser.parseFromString(raw, "text/html")

                unwrapNestedListItems(dom.body)

                props.onChange(dom.body.innerHTML, dom.body.textContent || "")
              })
            }}
          />
          <CodeHighlightPlugin />
          <ListPlugin />
          <LinkPlugin />
          <HorizontalRulePlugin />
          <AutoLinkPlugin />
          <ListMaxIndentLevelPlugin maxDepth={7} />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          <CmdEnterPlugin />
          {children}
        </div>
      </div>
    </LexicalComposer>
  )
}
